import { Event, Task } from '@/types/scheduler';
import { nowInLocal, fromApiFormat } from './timezone';
import { isAfter, isBefore, differenceInMinutes, differenceInHours, format } from 'date-fns';

export type EventTimeStatus = 'missed' | 'current' | 'next' | 'upcoming' | 'future';
export type TaskTimeStatus = 'overdue' | 'due-today' | 'due-soon' | 'future';

export interface EventWithStatus extends Event {
  timeStatus: EventTimeStatus;
  timeDescription: string;
  isNext?: boolean;
}

export interface TaskWithStatus extends Task {
  timeStatus: TaskTimeStatus;
  timeDescription: string;
}

export const getEventTimeStatus = (event: Event, now: Date = nowInLocal()): EventTimeStatus => {
  const startTime = fromApiFormat(event.start_datetime);
  const endTime = event.end_datetime ? fromApiFormat(event.end_datetime) : startTime;
  
  // Check if event is currently happening
  if (!isBefore(now, startTime) && !isAfter(now, endTime)) {
    return 'current';
  }
  
  // Check if event has passed
  if (isAfter(now, endTime)) {
    return 'missed';
  }
  
  // Event is in the future
  const minutesUntil = differenceInMinutes(startTime, now);
  
  if (minutesUntil <= 60) {
    return 'next';
  } else if (minutesUntil <= 1440) { // 24 hours
    return 'upcoming';
  }
  
  return 'future';
};

export const getTaskTimeStatus = (task: Task, now: Date = nowInLocal()): TaskTimeStatus => {
  if (!task.due_date) return 'future';
  
  const dueDate = fromApiFormat(task.due_date);
  const hoursUntil = differenceInHours(dueDate, now);
  
  if (isAfter(now, dueDate)) {
    return 'overdue';
  }
  
  if (hoursUntil <= 24) {
    return 'due-today';
  }
  
  if (hoursUntil <= 72) {
    return 'due-soon';
  }
  
  return 'future';
};

export const getEventTimeDescription = (event: Event, now: Date = nowInLocal()): string => {
  const startTime = fromApiFormat(event.start_datetime);
  const endTime = event.end_datetime ? fromApiFormat(event.end_datetime) : startTime;
  const status = getEventTimeStatus(event, now);
  
  switch (status) {
    case 'current':
      const minutesRemaining = differenceInMinutes(endTime, now);
      return minutesRemaining > 0 ? `${minutesRemaining}m remaining` : 'Ending now';
    
    case 'missed':
      const minutesAgo = differenceInMinutes(now, endTime);
      if (minutesAgo < 60) {
        return `${minutesAgo}m ago`;
      } else {
        const hoursAgo = Math.floor(minutesAgo / 60);
        return `${hoursAgo}h ago`;
      }
    
    case 'next':
      const minutesUntil = differenceInMinutes(startTime, now);
      if (minutesUntil < 60) {
        return `in ${minutesUntil}m`;
      } else {
        return `in ${Math.floor(minutesUntil / 60)}h ${minutesUntil % 60}m`;
      }
    
    case 'upcoming':
      const hoursUntil = differenceInHours(startTime, now);
      return `in ${hoursUntil}h`;
    
    default:
      return format(startTime, 'h:mm a');
  }
};

export const getTaskTimeDescription = (task: Task, now: Date = nowInLocal()): string => {
  if (!task.due_date) return '';
  
  const dueDate = fromApiFormat(task.due_date);
  const status = getTaskTimeStatus(task, now);
  
  switch (status) {
    case 'overdue':
      const hoursOverdue = differenceInHours(now, dueDate);
      if (hoursOverdue < 24) {
        return `${hoursOverdue}h overdue`;
      } else {
        const daysOverdue = Math.floor(hoursOverdue / 24);
        return `${daysOverdue}d overdue`;
      }
    
    case 'due-today':
      const hoursUntil = differenceInHours(dueDate, now);
      if (hoursUntil < 1) {
        const minutesUntil = differenceInMinutes(dueDate, now);
        return `due in ${minutesUntil}m`;
      }
      return `due in ${hoursUntil}h`;
    
    case 'due-soon':
      const hoursLeft = differenceInHours(dueDate, now);
      const daysLeft = Math.floor(hoursLeft / 24);
      return `due in ${daysLeft}d`;
    
    default:
      return format(dueDate, 'MMM d');
  }
};

export const sortEventsByTime = (events: Event[], now: Date = nowInLocal()): EventWithStatus[] => {
  const eventsWithStatus: EventWithStatus[] = events.map(event => ({
    ...event,
    timeStatus: getEventTimeStatus(event, now),
    timeDescription: getEventTimeDescription(event, now),
  }));

  // Find the next upcoming event
  const upcomingEvents = eventsWithStatus.filter(e => 
    e.timeStatus === 'next' || e.timeStatus === 'upcoming'
  );
  
  if (upcomingEvents.length > 0) {
    const nextEvent = upcomingEvents.reduce((closest, current) => {
      const closestStart = fromApiFormat(closest.start_datetime);
      const currentStart = fromApiFormat(current.start_datetime);
      return isBefore(currentStart, closestStart) ? current : closest;
    });
    nextEvent.isNext = true;
  }

  // Sort by priority: current > next > upcoming > future > missed
  const statusOrder = { current: 0, next: 1, upcoming: 2, future: 3, missed: 4 };
  
  return eventsWithStatus.sort((a, b) => {
    const statusDiff = statusOrder[a.timeStatus] - statusOrder[b.timeStatus];
    if (statusDiff !== 0) return statusDiff;
    
    // Within same status, sort by time
    const aTime = fromApiFormat(a.start_datetime);
    const bTime = fromApiFormat(b.start_datetime);
    
    if (a.timeStatus === 'missed') {
      return isAfter(aTime, bTime) ? -1 : 1; // Most recent missed first
    }
    
    return isBefore(aTime, bTime) ? -1 : 1; // Earliest first
  });
};

export const sortTasksByTime = (tasks: Task[], now: Date = nowInLocal()): TaskWithStatus[] => {
  const tasksWithStatus = tasks.map(task => ({
    ...task,
    timeStatus: getTaskTimeStatus(task, now),
    timeDescription: getTaskTimeDescription(task, now),
  }));

  // Sort by priority: overdue > due-today > due-soon > future
  const statusOrder = { overdue: 0, 'due-today': 1, 'due-soon': 2, future: 3 };
  
  return tasksWithStatus.sort((a, b) => {
    const statusDiff = statusOrder[a.timeStatus] - statusOrder[b.timeStatus];
    if (statusDiff !== 0) return statusDiff;
    
    // Within same status, sort by due date
    if (a.due_date && b.due_date) {
      const aTime = fromApiFormat(a.due_date);
      const bTime = fromApiFormat(b.due_date);
      return isBefore(aTime, bTime) ? -1 : 1;
    }
    
    return 0;
  });
};